import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
} from "@angular/core";
import { CartUploadService } from "../../../core/providers/cart-upload/cart-upload.service";
import { parseEanList } from "../../utils/parse-ean-list";

type EanSearchTab = "paste" | "upload";

@Component({
    selector: "vsf-ean-search-modal",
    templateUrl: "./ean-search-modal.component.html",
    styleUrls: ["./ean-search-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EanSearchModalComponent {
    @Input() visible = false;
    @Output() close = new EventEmitter<void>();
    @Output() search = new EventEmitter<string>();

    @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;

    activeTab: EanSearchTab = "paste";
    eanText = "";
    selectedFile: File | null = null;
    isDragOver = false;
    isUploading = false;
    fileError = "";
    pasteError = "";

    constructor(
        private cd: ChangeDetectorRef,
        private cartUploadService: CartUploadService,
    ) {}

    selectTab(tab: EanSearchTab) {
        this.activeTab = tab;
        this.fileError = "";
        this.pasteError = "";
        this.cd.markForCheck();
    }

    onCancel() {
        this.resetState();
        this.close.emit();
    }

    async onUpload() {
        if (this.activeTab === "upload") {
            await this.searchFromFile();
            return;
        }

        const eans = parseEanList(this.eanText);
        if (!eans) {
            this.pasteError =
                "No EAN/GTIN codes found. Paste one code per line or separated by commas.";
            this.cd.markForCheck();
            return;
        }
        this.search.emit(eans);
        this.resetState();
    }

    get canUpload(): boolean {
        if (this.isUploading) {
            return false;
        }
        if (this.activeTab === "upload") {
            return !!this.selectedFile;
        }
        return parseEanList(this.eanText).length > 0;
    }

    triggerFileInput() {
        this.fileInput?.nativeElement.click();
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) {
            this.setSelectedFile(file);
        }
        input.value = "";
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
        this.cd.markForCheck();
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
        this.cd.markForCheck();
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
        const file = event.dataTransfer?.files?.[0];
        if (file) {
            this.setSelectedFile(file);
        }
        this.cd.markForCheck();
    }

    private setSelectedFile(file: File) {
        if (!this.isValidFileType(file)) {
            this.selectedFile = null;
            this.fileError =
                "Unsupported file type. Please upload a .csv, .xls, or .xlsx file.";
            this.cd.markForCheck();
            return;
        }
        this.selectedFile = file;
        this.fileError = "";
        this.cd.markForCheck();
    }

    private async searchFromFile() {
        if (!this.selectedFile) {
            return;
        }

        this.isUploading = true;
        this.fileError = "";
        this.cd.markForCheck();

        try {
            const eans = await this.cartUploadService.readEanCodesFromFile(
                this.selectedFile,
            );
            const parsed = parseEanList(eans.join(","));
            if (!parsed) {
                this.fileError = "No EAN/GTIN codes found in the file.";
                this.isUploading = false;
                this.cd.markForCheck();
                return;
            }
            this.search.emit(parsed);
            this.resetState();
        } catch (error: any) {
            this.isUploading = false;
            this.fileError =
                error?.message || "Failed to read file. Please try again.";
            this.cd.markForCheck();
        }
    }

    private isValidFileType(file: File): boolean {
        const name = file.name.toLowerCase();
        if (
            name.endsWith(".csv") ||
            name.endsWith(".txt") ||
            name.endsWith(".xls") ||
            name.endsWith(".xlsx")
        ) {
            return true;
        }

        return [
            "text/csv",
            "application/csv",
            "text/comma-separated-values",
            "text/plain",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ].includes(file.type);
    }

    private resetState() {
        this.eanText = "";
        this.activeTab = "paste";
        this.selectedFile = null;
        this.isDragOver = false;
        this.isUploading = false;
        this.fileError = "";
        this.pasteError = "";
        this.cd.markForCheck();
    }
}
